/**
 * Auth-Store (Zustand).
 *
 * Access-Token wird NUR im Memory gehalten (XSS-Mitigation).
 * Refresh-Token sitzt als httponly-Cookie beim Backend; der Browser schickt
 * es bei `withCredentials: true` automatisch mit zu /auth/refresh.
 *
 * Konsequenz: Beim Page-Reload haben wir kein Access-Token → der App-Loader
 * versucht `/auth/refresh` einmal beim Mount und lädt die User-Info nach.
 */
import { create } from "zustand";

import type { UserResponse } from "@/api/auth";

interface AuthState {
  accessToken: string | null;
  user: UserResponse | null;
  isAuthenticated: boolean;
  isHydrating: boolean;

  setAccessToken: (token: string | null) => void;
  setUser: (user: UserResponse | null) => void;
  setHydrating: (value: boolean) => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  accessToken: null,
  user: null,
  isAuthenticated: false,
  isHydrating: true,

  setAccessToken: (token) =>
    set((state) => ({
      accessToken: token,
      isAuthenticated: !!token && !!state.user,
    })),

  setUser: (user) =>
    set((state) => ({
      user,
      isAuthenticated: !!user && !!state.accessToken,
    })),

  setHydrating: (value) => set({ isHydrating: value }),

  clear: () =>
    set({
      accessToken: null,
      user: null,
      isAuthenticated: false,
      isHydrating: false,
    }),
}));
